import React from 'react';
import { useIntl } from 'react-intl';
import { MapPin, WifiHigh, Briefcase, Translate, Users, GraduationCap, Clock } from '@phosphor-icons/react';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';

export interface MetaGridProps {
  location?: string;
  remoteness?: string;
  field?: string;
  jobType?: string;
  normalisedRoleLevel?: string;
  requiredLanguages?: string[];
  peopleManagementTier?: string;
  educationLevel?: string;
  minimumYearsOfExperience?: number | null;
}

/**
 * 2-column icon+label grid (desktop) matching the redesign's meta block; collapses to a single
 * flowing text line on narrow widths via CSS. Only already-computed values are passed in — no
 * new data derivation, all fields already available/formatted in +Page.tsx.
 */
export function MetaGrid({
  location,
  remoteness,
  field,
  jobType,
  normalisedRoleLevel,
  requiredLanguages,
  peopleManagementTier,
  educationLevel,
  minimumYearsOfExperience,
}: MetaGridProps): React.ReactElement | null {
  const intl = useIntl();
  const rows: { key: string; icon: React.ReactElement; label: string }[] = [];

  if (location) rows.push({ key: 'location', icon: <MapPin className="h-3.5 w-3.5" weight="duotone" />, label: fixMojibake(location) });
  if (remoteness || field) {
    rows.push({
      key: 'remote-field',
      icon: <WifiHigh className="h-3.5 w-3.5" weight="duotone" />,
      label: [remoteness, field].filter((v): v is string => Boolean(v)).map(fixMojibake).join(' · '),
    });
  }
  if (jobType || normalisedRoleLevel) {
    rows.push({
      key: 'jobtype-level',
      icon: <Briefcase className="h-3.5 w-3.5" weight="duotone" />,
      label: [jobType, normalisedRoleLevel].filter((v): v is string => Boolean(v)).map(fixMojibake).join(' · '),
    });
  }
  if (requiredLanguages && requiredLanguages.length > 0) {
    rows.push({ key: 'languages', icon: <Translate className="h-3.5 w-3.5" weight="duotone" />, label: requiredLanguages.map(fixMojibake).join(', ') });
  }
  if (peopleManagementTier) {
    rows.push({ key: 'management', icon: <Users className="h-3.5 w-3.5" weight="duotone" />, label: fixMojibake(peopleManagementTier) });
  }
  if (educationLevel) {
    rows.push({ key: 'education', icon: <GraduationCap className="h-3.5 w-3.5" weight="duotone" />, label: fixMojibake(educationLevel) });
  }
  if (minimumYearsOfExperience != null && minimumYearsOfExperience > 0) {
    rows.push({
      key: 'experience',
      icon: <Clock className="h-3.5 w-3.5" weight="duotone" />,
      label: intl.formatMessage(
        { id: 'jobDetailPageV2.experienceYears', defaultMessage: '{years} {years, plural, one {year} other {years}} experience', description: 'Minimum years of experience, shown in the redesigned job detail meta grid' },
        { years: minimumYearsOfExperience },
      ),
    });
  }

  if (rows.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
      {rows.map((row) => (
        <div key={row.key} className="flex items-center gap-1.5 text-[13px] font-medium text-[#4A4438] dark:text-[#C4A882]">
          <span className="flex-none text-[#8C8474]">{row.icon}</span>
          <span>{row.label}</span>
        </div>
      ))}
    </div>
  );
}
